import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import InputWithIcon from '../../components/InputWithIcon';
import DatePicker from '../../components/DatePicker';
import UploadErrors from '../../components/UploadErrors';
import { dialogActionsStyle, primaryButtonStyle, secondaryButtonStyle } from '../../utils/sharedStyles';
import protocolIcon from '../../assets/protocols/protocol.svg';
import summaryIcon from '../../assets/protocols/summary.svg';
import authorIcon from '../../assets/protocols/author.svg';
import FolderSelect from './FolderSelect';
import { toPickerDate, toServerDate } from './documentDate';

/**
 * One small form for every naming action on the page: a new folder, editing a
 * folder, and editing a document. `placeholder` names the name field; the
 * dialog's title says the rest. Pass `folderOptions` to also offer a
 * destination folder ([{ id, label }], id null = the root), `withDescription`
 * for a folder's optional description, and `withDocumentDetails` for a
 * document's date (ISO in, YYYY-MM-DD out, '' = none) and author.
 */
const NameDialog = ({
  open, title, placeholder, initialName = '', withDescription = false, initialDescription = '',
  withDocumentDetails = false, initialDate = '', initialAuthor = '',
  folderOptions, initialFolder = null, submitText = 'שמירה', onSubmit, onClose,
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [date, setDate] = useState(toPickerDate(initialDate));
  const [author, setAuthor] = useState(initialAuthor);
  const [folder, setFolder] = useState(initialFolder);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setDate(toPickerDate(initialDate));
      setAuthor(initialAuthor);
      setFolder(initialFolder);
      setError('');
    }
  }, [open, initialName, initialDescription, initialDate, initialAuthor, initialFolder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('יש להזין שם');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        name: name.trim(),
        folder,
        ...(withDescription && { description: description.trim() }),
        ...(withDocumentDetails && { documentDate: toServerDate(date), author: author.trim() }),
      });
    } catch (err) {
      setError(err?.serverError?.hebMessage || 'השמירה נכשלה. אנא נסה שנית.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <div className="dd-dialog-fields">
            <InputWithIcon
              autoFocus
              icon={protocolIcon}
              placeholder={placeholder}
              aria-label={placeholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {withDescription && (
              <InputWithIcon
                icon={summaryIcon}
                placeholder="תיאור (לא חובה)"
                aria-label="תיאור"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
            {withDocumentDetails && (
              <>
                <DatePicker placeholder="תאריך המסמך - DD/MM/YYYY" value={date} onChange={setDate} />
                <InputWithIcon
                  icon={authorIcon}
                  placeholder="נכתב על ידי"
                  aria-label="נכתב על ידי"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
              </>
            )}
            {folderOptions && (
              <FolderSelect options={folderOptions} value={folder} onChange={setFolder} />
            )}
          </div>
          <UploadErrors messages={error ? [error] : []} onDismiss={() => setError('')} autoDismissMs={0} />
        </DialogContent>
        <DialogActions style={dialogActionsStyle}>
          <Button style={secondaryButtonStyle} onClick={onClose} disabled={saving}>ביטול</Button>
          <Button style={primaryButtonStyle} type="submit" disabled={saving}>
            {saving ? 'שומר...' : submitText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

NameDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  initialName: PropTypes.string,
  withDescription: PropTypes.bool,
  initialDescription: PropTypes.string,
  withDocumentDetails: PropTypes.bool,
  initialDate: PropTypes.string,
  initialAuthor: PropTypes.string,
  folderOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })),
  initialFolder: PropTypes.string,
  submitText: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default NameDialog;
